import React, { useEffect, useMemo, useState } from "react";
import { AdminAuthAPI } from "../../api/adminAuth";
import { useAdminAuth } from "../../auth/AdminAuthContext";
import { AdminInvite, AdminRole, AdminUser } from "../../types/adminAuth";

type UserEditState = Record<
  string,
  {
    role: AdminRole;
    isActive: boolean;
  }
>;

const ROLE_LABELS: Record<AdminRole, string> = {
  super_admin: "Super Admin",
  manager: "Manager",
};

export default function AdminUsersPage() {
  const { user } = useAdminAuth();
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState<string | null>(null);
  const [inviting, setInviting] = useState(false);
  const [revokingId, setRevokingId] = useState<string | null>(null);
  const [changingPassword, setChangingPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [inviteNotice, setInviteNotice] = useState<string | null>(null);
  const [passwordNotice, setPasswordNotice] = useState<string | null>(null);
  const [items, setItems] = useState<AdminUser[]>([]);
  const [invites, setInvites] = useState<AdminInvite[]>([]);
  const [edits, setEdits] = useState<UserEditState>({});
  const [inviteFullName, setInviteFullName] = useState("");
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState<AdminRole>("manager");
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [usersData, invitesData] = await Promise.all([
        AdminAuthAPI.listUsers(),
        AdminAuthAPI.listInvites(),
      ]);
      const users = Array.isArray(usersData.items) ? usersData.items : [];
      const pendingInvites = Array.isArray(invitesData.items) ? invitesData.items : [];
      setItems(users);
      setInvites(pendingInvites);
      const nextEdits: UserEditState = {};
      for (const nextUser of users) {
        nextEdits[nextUser.id] = {
          role: nextUser.role,
          isActive: Boolean(nextUser.isActive),
        };
      }
      setEdits(nextEdits);
    } catch (e: any) {
      setError(
        e?.response?.data?.message || e?.message || "Failed to load admin users.",
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const canInvite = useMemo(
    () => !!inviteFullName.trim() && !!inviteEmail.trim(),
    [inviteEmail, inviteFullName],
  );

  const canChangePassword = useMemo(
    () =>
      !!currentPassword &&
      !!newPassword &&
      !!confirmPassword &&
      newPassword === confirmPassword,
    [confirmPassword, currentPassword, newPassword],
  );

  const onInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canInvite) return;
    setInviting(true);
    setError(null);
    setInviteNotice(null);
    try {
      await AdminAuthAPI.inviteUser({
        fullName: inviteFullName.trim(),
        email: inviteEmail.trim(),
        role: inviteRole,
      });
      setInviteFullName("");
      setInviteEmail("");
      setInviteRole("manager");
      setInviteNotice(`Invite sent to ${inviteEmail.trim()}.`);
      await loadData();
    } catch (e: any) {
      setError(
        e?.response?.data?.message || e?.message || "Failed to send admin invite.",
      );
    } finally {
      setInviting(false);
    }
  };

  const onRevokeInvite = async (invite: AdminInvite) => {
    setRevokingId(invite.id);
    setError(null);
    setInviteNotice(null);
    try {
      await AdminAuthAPI.revokeInvite(invite.id);
      setInviteNotice(`Invite revoked for ${invite.email}.`);
      await loadData();
    } catch (e: any) {
      setError(
        e?.response?.data?.message || e?.message || "Failed to revoke invite.",
      );
    } finally {
      setRevokingId(null);
    }
  };

  const onSaveUser = async (targetUser: AdminUser) => {
    const draft = edits[targetUser.id];
    if (!draft) return;
    const roleChanged = draft.role !== targetUser.role;
    const activeChanged = draft.isActive !== targetUser.isActive;
    if (!roleChanged && !activeChanged) return;

    setSavingId(targetUser.id);
    setError(null);
    try {
      await AdminAuthAPI.updateUser(targetUser.id, {
        role: draft.role,
        isActive: draft.isActive,
      });
      await loadData();
    } catch (e: any) {
      setError(
        e?.response?.data?.message || e?.message || "Failed to update user.",
      );
    } finally {
      setSavingId(null);
    }
  };

  const onChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canChangePassword) {
      setError("Passwords must match before you can save.");
      return;
    }

    setChangingPassword(true);
    setError(null);
    setPasswordNotice(null);
    try {
      await AdminAuthAPI.changeMyPassword({
        currentPassword,
        newPassword,
      });
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      setPasswordNotice("Super admin password updated.");
    } catch (e: any) {
      setError(
        e?.response?.data?.message ||
          e?.message ||
          "Failed to change password.",
      );
    } finally {
      setChangingPassword(false);
    }
  };

  return (
    <div style={{ display: "grid", gap: 16 }}>
      <section style={cardStyle}>
        <h1 style={{ margin: 0 }}>Admin Access</h1>
        <p style={{ marginTop: 6, color: "#475569", fontSize: 13 }}>
          Direct account creation is disabled after the first super admin is set
          up. Invite admins by role, manage active accounts, and update the current
          super admin password here.
        </p>

        <div style={panelGridStyle}>
          <div style={subCardStyle}>
            <h2 style={{ margin: 0, fontSize: 18 }}>Invite Admin</h2>
            <p style={subtleTextStyle}>
              Send an email invite from <strong>support@beyondpendants.com</strong>.
              The invitee must create a strong password before they can log in.
            </p>

            <form onSubmit={onInvite} style={{ display: "grid", gap: 10 }}>
              <input
                value={inviteFullName}
                onChange={(e) => setInviteFullName(e.target.value)}
                placeholder="Full name"
                style={inputStyle}
              />
              <div style={rowStyle}>
                <input
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  placeholder="Email"
                  type="email"
                  style={inputStyle}
                />
                <select
                  value={inviteRole}
                  onChange={(e) => setInviteRole(e.target.value as AdminRole)}
                  style={inputStyle}
                >
                  <option value="manager">Manager</option>
                  <option value="super_admin">Super Admin</option>
                </select>
              </div>
              <button
                type="submit"
                disabled={!canInvite || inviting}
                style={{
                  ...primaryButtonStyle,
                  width: 180,
                  opacity: !canInvite || inviting ? 0.7 : 1,
                }}
              >
                {inviting ? "Sending..." : "Send Invite"}
              </button>
            </form>

            {inviteNotice ? (
              <div style={successBoxStyle}>{inviteNotice}</div>
            ) : null}
          </div>

          <div style={subCardStyle}>
            <h2 style={{ margin: 0, fontSize: 18 }}>Change Super Admin Password</h2>
            <p style={subtleTextStyle}>
              Signed in as <strong>{user?.fullName || "Super Admin"}</strong>. Use
              a strong password with uppercase, lowercase, number, and special
              character.
            </p>

            <form onSubmit={onChangePassword} style={{ display: "grid", gap: 10 }}>
              <input
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                placeholder="Current password"
                type="password"
                style={inputStyle}
              />
              <input
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="New password"
                type="password"
                style={inputStyle}
              />
              <input
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm new password"
                type="password"
                style={inputStyle}
              />
              <button
                type="submit"
                disabled={!canChangePassword || changingPassword}
                style={{
                  ...primaryButtonStyle,
                  width: 250,
                  opacity: !canChangePassword || changingPassword ? 0.7 : 1,
                }}
              >
                {changingPassword ? "Updating..." : "Update Password"}
              </button>
            </form>

            {passwordNotice ? (
              <div style={successBoxStyle}>{passwordNotice}</div>
            ) : null}
          </div>
        </div>

        {error ? <div style={errorBoxStyle}>{error}</div> : null}
      </section>

      <section style={cardStyle}>
        <h2 style={{ marginTop: 0 }}>Pending Invites</h2>
        {loading ? <div>Loading invites...</div> : null}
        {!loading && !invites.length ? (
          <div style={{ color: "#64748b", fontSize: 13 }}>No pending invites.</div>
        ) : null}

        {!loading && invites.length > 0 ? (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 720 }}>
              <thead>
                <tr>
                  <th style={thStyle}>Name</th>
                  <th style={thStyle}>Email</th>
                  <th style={thStyle}>Role</th>
                  <th style={thStyle}>Sent</th>
                  <th style={thStyle}>Expires</th>
                  <th style={thStyle}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {invites.map((invite) => (
                  <tr key={invite.id}>
                    <td style={tdStyle}>{invite.fullName}</td>
                    <td style={tdStyle}>{invite.email}</td>
                    <td style={tdStyle}>{ROLE_LABELS[invite.role]}</td>
                    <td style={tdStyle}>
                      {invite.createdAt
                        ? new Date(invite.createdAt).toLocaleString()
                        : "Unknown"}
                    </td>
                    <td style={tdStyle}>
                      {invite.expiresAt
                        ? new Date(invite.expiresAt).toLocaleString()
                        : "Unknown"}
                    </td>
                    <td style={tdStyle}>
                      <button
                        type="button"
                        disabled={revokingId === invite.id}
                        onClick={() => onRevokeInvite(invite)}
                        style={{
                          ...secondaryButtonStyle,
                          opacity: revokingId === invite.id ? 0.7 : 1,
                        }}
                      >
                        {revokingId === invite.id ? "Revoking..." : "Revoke"}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </section>

      <section style={cardStyle}>
        <h2 style={{ marginTop: 0 }}>Existing Accounts</h2>
        {loading ? <div>Loading admin users...</div> : null}
        {!loading && !items.length ? <div>No admin users found.</div> : null}

        {!loading && items.length > 0 ? (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 760 }}>
              <thead>
                <tr>
                  <th style={thStyle}>Name</th>
                  <th style={thStyle}>Email</th>
                  <th style={thStyle}>Role</th>
                  <th style={thStyle}>Active</th>
                  <th style={thStyle}>Last Login</th>
                  <th style={thStyle}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map((nextUser) => {
                  const draft = edits[nextUser.id] || {
                    role: nextUser.role,
                    isActive: Boolean(nextUser.isActive),
                  };
                  const changed =
                    draft.role !== nextUser.role ||
                    draft.isActive !== Boolean(nextUser.isActive);

                  return (
                    <tr key={nextUser.id}>
                      <td style={tdStyle}>
                        <div style={{ display: "grid", gap: 4 }}>
                          <span>{nextUser.fullName}</span>
                          {nextUser.role === "super_admin" ? (
                            <span style={tagStyle}>Primary access</span>
                          ) : null}
                        </div>
                      </td>
                      <td style={tdStyle}>{nextUser.email}</td>
                      <td style={tdStyle}>
                        <select
                          value={draft.role}
                          onChange={(e) =>
                            setEdits((prev) => ({
                              ...prev,
                              [nextUser.id]: {
                                role: e.target.value as AdminRole,
                                isActive: draft.isActive,
                              },
                            }))
                          }
                          style={smallInputStyle}
                        >
                          <option value="manager">Manager</option>
                          <option value="super_admin">Super Admin</option>
                        </select>
                      </td>
                      <td style={tdStyle}>
                        <label
                          style={{
                            display: "inline-flex",
                            gap: 8,
                            alignItems: "center",
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={draft.isActive}
                            onChange={(e) =>
                              setEdits((prev) => ({
                                ...prev,
                                [nextUser.id]: {
                                  role: draft.role,
                                  isActive: e.target.checked,
                                },
                              }))
                            }
                          />
                          {draft.isActive ? "Active" : "Disabled"}
                        </label>
                      </td>
                      <td style={tdStyle}>
                        {nextUser.lastLoginAt
                          ? new Date(nextUser.lastLoginAt).toLocaleString()
                          : "Never"}
                      </td>
                      <td style={tdStyle}>
                        <button
                          type="button"
                          disabled={!changed || savingId === nextUser.id}
                          onClick={() => onSaveUser(nextUser)}
                          style={{
                            ...secondaryButtonStyle,
                            opacity: !changed || savingId === nextUser.id ? 0.7 : 1,
                          }}
                        >
                          {savingId === nextUser.id ? "Saving..." : "Save"}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : null}
      </section>
    </div>
  );
}

const cardStyle: React.CSSProperties = {
  background: "#fff",
  border: "1px solid #e5e7eb",
  borderRadius: 14,
  padding: 16,
};

const subCardStyle: React.CSSProperties = {
  border: "1px solid #e5e7eb",
  borderRadius: 12,
  padding: 16,
  display: "grid",
  gap: 12,
  background: "#f8fafc",
};

const panelGridStyle: React.CSSProperties = {
  display: "grid",
  gap: 14,
  gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
  marginTop: 12,
};

const rowStyle: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "1fr 1fr",
  gap: 10,
};

const inputStyle: React.CSSProperties = {
  border: "1px solid #cbd5e1",
  borderRadius: 10,
  padding: "10px 12px",
  fontSize: 14,
  width: "100%",
  boxSizing: "border-box",
};

const smallInputStyle: React.CSSProperties = {
  border: "1px solid #cbd5e1",
  borderRadius: 8,
  padding: "6px 8px",
  fontSize: 13,
};

const thStyle: React.CSSProperties = {
  textAlign: "left",
  borderBottom: "1px solid #e5e7eb",
  padding: "8px 10px",
  fontSize: 12,
  color: "#64748b",
  fontWeight: 700,
};

const tdStyle: React.CSSProperties = {
  borderBottom: "1px solid #f1f5f9",
  padding: "10px",
  fontSize: 13,
  verticalAlign: "middle",
};

const primaryButtonStyle: React.CSSProperties = {
  border: "none",
  borderRadius: 10,
  padding: "10px 12px",
  fontWeight: 700,
  color: "#fff",
  background: "#0f172a",
  cursor: "pointer",
};

const secondaryButtonStyle: React.CSSProperties = {
  border: "1px solid #cbd5e1",
  borderRadius: 8,
  padding: "6px 10px",
  fontSize: 12,
  fontWeight: 700,
  background: "#fff",
  cursor: "pointer",
};

const subtleTextStyle: React.CSSProperties = {
  margin: 0,
  color: "#475569",
  fontSize: 13,
  lineHeight: 1.6,
};

const errorBoxStyle: React.CSSProperties = {
  marginTop: 12,
  border: "1px solid #fecaca",
  background: "#fef2f2",
  color: "#b91c1c",
  borderRadius: 10,
  padding: "8px 10px",
  fontSize: 12,
};

const successBoxStyle: React.CSSProperties = {
  border: "1px solid #bbf7d0",
  background: "#f0fdf4",
  color: "#166534",
  borderRadius: 10,
  padding: "8px 10px",
  fontSize: 12,
};

const tagStyle: React.CSSProperties = {
  display: "inline-flex",
  width: "fit-content",
  borderRadius: 999,
  padding: "2px 8px",
  fontSize: 11,
  fontWeight: 700,
  background: "#e0f2fe",
  color: "#075985",
};
